"use client"

import { useState, useEffect, useRef } from "react"
import { Button, Input, Label, TabContent, TabPane, Nav, NavItem, NavLink } from "reactstrap"
import { Upload, XCircle, ChevronLeft, ChevronRight, Image as ImageIcon, Film, Play } from "react-feather"
import classnames from "classnames"
import styles from "./ImageGallery.module.scss"
import { updateMerchantDetails as updateMerchant } from "../../../../api-handle/merchantApi"
import { uploadMultipleFiles as uploadMultiFiles, uploadSingleFile } from "../../../../api-handle/fileApi"

const MAX_IMAGES = 5
const MAX_VIDEOS = 3
const MAX_VIDEO_MB = 200
const AUTO_SCROLL_MS = 4000

// Detect a real image/video from the browser-reported MIME type. Anything else
// (renamed executables, pdfs, "false" images, etc.) returns null and is rejected.
const kindOf = (file) =>
    file?.type?.startsWith("image/") ? "image" : file?.type?.startsWith("video/") ? "video" : null

const ImageGallery = ({ merchantData, setRefetch }) => {
    const BaseUrl = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/+$/, "")
    const toUrl = (path) => (path ? `${BaseUrl}/${String(path).replace(/^\/+/, "")}` : "")

    // Backend returns `type` as populated Type refs ({ _id, key, title }); use the
    // keys since the detail objects are named `${key}Detail`.
    const types = (merchantData?.type || [])
        .map((t) => (t && typeof t === "object" ? t.key : t))
        .filter(Boolean)

    // One unified media list per type: existing images + existing videos, each
    // tagged with its kind. New uploads are appended with isExisting:false + file.
    const buildInitialMedia = (type) => {
        const detail = merchantData?.[`${type}Detail`] || {}
        const imgs = (detail.images || []).map((p) => ({ kind: "image", src: toUrl(p), isExisting: true, originalPath: p }))
        const vids = (detail.videos || []).map((p) => ({ kind: "video", src: toUrl(p), isExisting: true, originalPath: p }))
        return [...imgs, ...vids]
    }

    const [activeTab, setActiveTab] = useState(types[0])
    const [media, setMedia] = useState(
        types.reduce((acc, type) => {
            acc[type] = buildInitialMedia(type)
            return acc
        }, {}),
    )
    const [currentIndex, setCurrentIndex] = useState(0)
    const [error, setError] = useState("")
    const [isChanged, setIsChanged] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const pausedRef = useRef(false)

    // ---- Cover image (gym / academy only) ----
    const coverTypes = types.filter((t) => t !== "marketplace")
    const [coverImages, setCoverImages] = useState(
        coverTypes.reduce((acc, type) => {
            acc[type] = merchantData?.[`${type}Detail`]?.coverImage || ""
            return acc
        }, {}),
    )
    const [newCoverFile, setNewCoverFile] = useState(coverTypes.reduce((acc, t) => ((acc[t] = null), acc), {}))
    const [coverRemoved, setCoverRemoved] = useState(coverTypes.reduce((acc, t) => ((acc[t] = false), acc), {}))
    const [coverBlobUrl, setCoverBlobUrl] = useState(coverTypes.reduce((acc, t) => ((acc[t] = null), acc), {}))

    const countKind = (list, kind) => (list || []).filter((m) => m.kind === kind).length
    const slides = media[activeTab] || []

    // Reset the carousel position when switching tabs.
    useEffect(() => {
        setCurrentIndex(0)
    }, [activeTab])

    // Keep the index in range as items are added/removed.
    useEffect(() => {
        if (currentIndex > slides.length - 1) setCurrentIndex(Math.max(0, slides.length - 1))
    }, [slides.length, currentIndex])

    // Auto-scroll the preview slider (pauses on hover). A steady cadence — the
    // functional update means we never need currentIndex in the deps.
    useEffect(() => {
        if (slides.length <= 1) return
        const id = setInterval(() => {
            if (!pausedRef.current) setCurrentIndex((i) => (i + 1) % slides.length)
        }, AUTO_SCROLL_MS)
        return () => clearInterval(id)
    }, [activeTab, slides.length])

    const getCoverPreviewSrc = (type) => {
        if (coverRemoved[type]) return null
        if (coverBlobUrl[type]) return coverBlobUrl[type]
        if (coverImages[type]) return toUrl(coverImages[type])
        return null
    }

    const handleCoverUpload = (e, type) => {
        const file = e?.target?.files?.[0]
        e.target.value = ""
        if (!file) return
        if (!file.type.startsWith("image/")) {
            setError("Cover must be an image.")
            return
        }
        if (coverBlobUrl[type]) URL.revokeObjectURL(coverBlobUrl[type])
        setCoverBlobUrl((prev) => ({ ...prev, [type]: URL.createObjectURL(file) }))
        setNewCoverFile((prev) => ({ ...prev, [type]: file }))
        setCoverRemoved((prev) => ({ ...prev, [type]: false }))
        setIsChanged(true)
    }

    const handleCoverRemove = (type) => {
        if (coverBlobUrl[type]) {
            URL.revokeObjectURL(coverBlobUrl[type])
            setCoverBlobUrl((prev) => ({ ...prev, [type]: null }))
        }
        setNewCoverFile((prev) => ({ ...prev, [type]: null }))
        setCoverRemoved((prev) => ({ ...prev, [type]: true }))
        setIsChanged(true)
    }

    useEffect(() => {
        const hasPendingCover = coverTypes.some((t) => newCoverFile[t] || coverRemoved[t])
        if (!hasPendingCover) {
            setCoverImages(
                coverTypes.reduce((acc, type) => {
                    acc[type] = merchantData?.[`${type}Detail`]?.coverImage || ""
                    return acc
                }, {}),
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantData?.gymDetail?.coverImage, merchantData?.academyDetail?.coverImage, newCoverFile, coverRemoved])

    // ---- Unified media add / remove ----
    const handleAddMedia = (e, type) => {
        setError("")
        const files = Array.from(e.target.files || [])
        e.target.value = ""
        if (!files.length) return

        // Reject anything that isn't a real image or video ("false" files).
        const accepted = files.filter((f) => kindOf(f))
        if (accepted.length !== files.length) {
            setError("Only image and video files are allowed.")
        }

        let imgCount = countKind(media[type], "image")
        let vidCount = countKind(media[type], "video")
        const additions = []

        for (const file of accepted) {
            const kind = kindOf(file)
            if (kind === "image") {
                if (imgCount >= MAX_IMAGES) {
                    setError(`You can upload a maximum of ${MAX_IMAGES} images.`)
                    continue
                }
                imgCount++
            } else {
                if (vidCount >= MAX_VIDEOS) {
                    setError(`You can upload a maximum of ${MAX_VIDEOS} videos.`)
                    continue
                }
                if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
                    setError(`Each video must be ${MAX_VIDEO_MB}MB or smaller.`)
                    continue
                }
                vidCount++
            }
            additions.push({ kind, src: URL.createObjectURL(file), isExisting: false, file })
        }

        if (!additions.length) return
        setMedia((prev) => ({ ...prev, [type]: [...prev[type], ...additions] }))
        setIsChanged(true)
    }

    const handleRemoveMedia = (index, type) => {
        setMedia((prev) => {
            const item = prev[type][index]
            if (item && !item.isExisting) URL.revokeObjectURL(item.src)
            return { ...prev, [type]: prev[type].filter((_, i) => i !== index) }
        })
        setIsChanged(true)
    }

    const handlePrev = () => {
        setCurrentIndex((i) => (i === 0 ? slides.length - 1 : i - 1))
    }
    const handleNext = () => {
        setCurrentIndex((i) => (i === slides.length - 1 ? 0 : i + 1))
    }

    const handleSave = async () => {
        try {
            setIsSaving(true)
            setError("")

            // Upload new cover images first (gym / academy).
            const uploadedCoverByType = {}
            for (const type of coverTypes) {
                uploadedCoverByType[type] = newCoverFile[type] ? (await uploadSingleFile(newCoverFile[type])).fileName : null
            }

            // Build the payload per type: upload new media (keeping order), then
            // split the unified list back into images[] and videos[].
            const payload = {}
            for (const type of types) {
                const list = media[type] || []
                const newImageFiles = list.filter((m) => !m.isExisting && m.kind === "image").map((m) => m.file)
                const newVideoFiles = list.filter((m) => !m.isExisting && m.kind === "video").map((m) => m.file)
                const uploadedImages = newImageFiles.length ? await uploadMultiFiles(newImageFiles) : []
                const uploadedVideos = newVideoFiles.length ? await uploadMultiFiles(newVideoFiles) : []

                let ii = 0
                let vi = 0
                const finalImages = []
                const finalVideos = []
                for (const m of list) {
                    if (m.kind === "image") finalImages.push(m.isExisting ? m.originalPath : uploadedImages[ii++])
                    else finalVideos.push(m.isExisting ? m.originalPath : uploadedVideos[vi++])
                }

                let coverImage = merchantData[`${type}Detail`]?.coverImage ?? ""
                if (coverTypes.includes(type)) {
                    if (uploadedCoverByType[type]) coverImage = uploadedCoverByType[type]
                    else if (coverRemoved[type]) coverImage = ""
                }

                payload[`${type}Detail`] = {
                    ...merchantData[`${type}Detail`],
                    images: finalImages,
                    videos: finalVideos,
                    ...(coverTypes.includes(type) ? { coverImage } : {}),
                }
            }

            await updateMerchant(payload)

            // Refetch will rebuild state from the server; just clear dirty flags
            // and revoke any blob URLs we created.
            Object.values(media).forEach((list) =>
                list.forEach((m) => {
                    if (!m.isExisting) URL.revokeObjectURL(m.src)
                }),
            )
            coverTypes.forEach((type) => {
                if (coverBlobUrl[type]) URL.revokeObjectURL(coverBlobUrl[type])
            })
            setNewCoverFile(coverTypes.reduce((acc, t) => ((acc[t] = null), acc), {}))
            setCoverRemoved(coverTypes.reduce((acc, t) => ((acc[t] = false), acc), {}))
            setCoverBlobUrl(coverTypes.reduce((acc, t) => ((acc[t] = null), acc), {}))
            setIsChanged(false)
        } catch (err) {
            setError("Error saving media.")
            console.error("Save error:", err)
        } finally {
            setIsSaving(false)
            if (setRefetch) setRefetch((prev) => !prev)
        }
    }

    const renderSlide = (item) =>
        item.kind === "video" ? (
            <video src={item.src} className={styles.previewImage} controls preload="metadata" />
        ) : (
            <img src={item.src || "/placeholder.svg"} alt="Gallery Preview" className={styles.previewImage} />
        )

    return (
        <div className={styles.imageGalleryContainer}>
            <div className={styles.header}>
                <h4 className={styles.sectionTitle}>
                    <ImageIcon size={24} className={styles.titleIcon} />
                    Media Gallery
                </h4>
                <p className={styles.subtitle}>Manage your business images and videos across different categories</p>
            </div>

            {/* Tab Navigation */}
            <Nav tabs className={styles.customTabs}>
                {coverTypes.map((type, index) => (
                    <NavItem key={index}>
                        <NavLink
                            className={classnames(styles.tabLink, { [styles.active]: activeTab === type })}
                            onClick={() => setActiveTab(type)}
                        >
                            <span className={styles.tabText}>{`${type.charAt(0).toUpperCase() + type.slice(1)}`}</span>
                            <span className={styles.imageCount}>
                                {countKind(media[type], "image")}/{MAX_IMAGES} images · {countKind(media[type], "video")}/{MAX_VIDEOS} videos
                            </span>
                        </NavLink>
                    </NavItem>
                ))}
            </Nav>

            {/* Tab Content */}
            <TabContent activeTab={activeTab} className={styles.tabContent}>
                {types.map((type, index) => {
                    const list = media[type] || []
                    const imgCount = countKind(list, "image")
                    const vidCount = countKind(list, "video")
                    const atLimit = imgCount >= MAX_IMAGES && vidCount >= MAX_VIDEOS
                    return (
                        <TabPane key={index} tabId={type}>
                            <div className={styles.contentWrapper}>
                                {/* Cover Image (gym / academy only) */}
                                {coverTypes.includes(type) && (
                                    <div className={styles.coverSection}>
                                        <h6 className={styles.coverTitle}>Cover Image</h6>
                                        <div className={styles.coverBox}>
                                            <div className={styles.coverPreview}>
                                                {getCoverPreviewSrc(type) ? (
                                                    <img src={getCoverPreviewSrc(type)} alt="Cover" className={styles.coverImage} />
                                                ) : (
                                                    <div className={styles.coverPlaceholder}>
                                                        <ImageIcon size={32} className={styles.coverPlaceholderIcon} />
                                                        <span>No cover image</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.coverActions}>
                                                <Label className={styles.coverUploadBtn} htmlFor={`cover-input-${type}`}>
                                                    <Upload size={18} />
                                                    <span>Upload</span>
                                                </Label>
                                                <Input
                                                    id={`cover-input-${type}`}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleCoverUpload(e, type)}
                                                    hidden
                                                />
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    className={styles.coverDeleteBtn}
                                                    onClick={() => handleCoverRemove(type)}
                                                    disabled={!coverImages[type] && !newCoverFile[type]}
                                                >
                                                    <XCircle size={16} />
                                                    <span>Delete</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Unified Media Slider (images + videos, auto-scroll) */}
                                <div className={styles.imagePreviewSection}>
                                    <div
                                        className={styles.imagePreview}
                                        onMouseEnter={() => (pausedRef.current = true)}
                                        onMouseLeave={() => (pausedRef.current = false)}
                                    >
                                        {list.length > 0 ? (
                                            <>
                                                {list.length > 1 && (
                                                    <button className={`${styles.navArrow} ${styles.navArrowLeft}`} onClick={handlePrev} type="button">
                                                        <ChevronLeft size={20} />
                                                    </button>
                                                )}

                                                <div className={styles.imageContainer}>
                                                    {renderSlide(list[Math.min(currentIndex, list.length - 1)])}
                                                    <div className={styles.imageInfo}>
                                                        <span>
                                                            {(list[currentIndex]?.kind === "video" ? "Video " : "Image ")}
                                                            {Math.min(currentIndex, list.length - 1) + 1} of {list.length}
                                                        </span>
                                                    </div>
                                                </div>

                                                {list.length > 1 && (
                                                    <button className={`${styles.navArrow} ${styles.navArrowRight}`} onClick={handleNext} type="button">
                                                        <ChevronRight size={20} />
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            <div className={styles.placeholder}>
                                                <div className={styles.placeholderContent}>
                                                    <ImageIcon size={48} className={styles.placeholderIcon} />
                                                    <h5>No Media Yet</h5>
                                                    <p>Upload images or videos to get started</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Single Upload (images + videos) */}
                                <div className={styles.uploadSection}>
                                    <Label className={styles.uploadButton} htmlFor={`media-input-${type}`}>
                                        <div className={styles.uploadContent}>
                                            <Upload size={20} className={styles.uploadIcon} />
                                            <span className={styles.uploadText}>Add Media</span>
                                            <span className={styles.uploadSubtext}>
                                                Images &amp; videos — up to {MAX_IMAGES} images and {MAX_VIDEOS} videos ({MAX_VIDEO_MB}MB each)
                                            </span>
                                        </div>
                                        <Input
                                            id={`media-input-${type}`}
                                            type="file"
                                            accept="image/*,video/*"
                                            multiple
                                            onChange={(e) => handleAddMedia(e, type)}
                                            hidden
                                            disabled={atLimit}
                                        />
                                    </Label>

                                    {error && (
                                        <div className={styles.errorMessage}>
                                            <XCircle size={16} />
                                            <span>{error}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Unified Thumbnail Grid */}
                                {list.length > 0 && (
                                    <div className={styles.thumbnailSection}>
                                        <h6 className={styles.thumbnailTitle}>All Media</h6>
                                        <div className={styles.thumbnailGrid}>
                                            {list.map((item, i) => (
                                                <div
                                                    key={i}
                                                    className={classnames(styles.thumbnailItem, {
                                                        [styles.activeThumbnail]: i === currentIndex,
                                                        [styles.newImage]: !item.isExisting,
                                                    })}
                                                    onClick={() => setCurrentIndex(i)}
                                                >
                                                    {item.kind === "video" ? (
                                                        <>
                                                            <video src={item.src} className={styles.thumbnail} muted preload="metadata" />
                                                            <Play
                                                                size={28}
                                                                style={{
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    transform: "translate(-50%, -50%)",
                                                                    color: "#fff",
                                                                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,.6))",
                                                                    pointerEvents: "none",
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <img src={item.src || "/placeholder.svg"} alt={`Media ${i + 1}`} className={styles.thumbnail} />
                                                    )}
                                                    <button
                                                        className={styles.removeButton}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleRemoveMedia(i, type)
                                                        }}
                                                        type="button"
                                                    >
                                                        <XCircle size={16} />
                                                    </button>
                                                    <div className={styles.thumbnailOverlay}>
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                                                            {item.kind === "video" ? <Film size={12} /> : <ImageIcon size={12} />}
                                                            {i + 1}
                                                        </span>
                                                        {!item.isExisting && <span className={styles.newBadge}>NEW</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </TabPane>
                    )
                })}
            </TabContent>

            {/* Save Button */}
            {isChanged && (
                <div className={styles.saveSection}>
                    <Button color="success" className={styles.saveButton} onClick={handleSave} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                </div>
            )}
        </div>
    )
}

export default ImageGallery
